/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { of, Subject } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { CompositeDataService } from 'src/app/shared/services/data-api/composite-data.service';
import { RoutingService } from 'src/app/service/routing.service';
import { FeatureFlagsService } from 'src/app/shared/services/customer-api/feature-flags.service';
import { PortfolioApiService } from 'src/app/shared/services/customer-api/portfolio-api.service';
import { BreakpointService } from 'src/app/shared/services/breakpoints.service';
import { ScoreTimeSeriesService } from 'src/app/shared/services/data-api/score-time-series.service';
import { ScoreUtilsService } from 'src/app/shared/utils/score-utils.service';
import { getScoreType } from 'src/app/shared/utils/score-type-details';

/** Set by the parent once its entity/price fetch succeeded (legacy ordering). */
export interface PeergroupContext {
  qparam: any;
  entity: any;
}

/**
 * "Peergroup Comparison" mosaic extracted from chartmap-company. The
 * peergroup fetch itself still rides on `yukkApi.peergroupComparison()`
 * (a §D yukkapi-teardown item, not part of the scoresTimeSeries migration);
 * the per-entity score decoration already uses the v2 ScoreTimeSeriesService.
 */
@Component({
  selector: 'app-peergroup-comparison',
  templateUrl: './peergroup-comparison.component.html',
  styles: [':host { display: contents; }'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PeergroupComparisonComponent implements OnChanges {
  /** Live URL params (display bindings). */
  @Input() qparam: any;
  /** Gated refresh trigger — clears the previous peergroup (legacy reset). */
  @Input() fetchParams: any;
  /** Set after the parent's entity fetch succeeds; triggers the peergroup load. */
  @Input() context: PeergroupContext | null = null;
  @Input() visible = false;
  /** Mosaic state maps shared with the parent (parent initializes per refresh). */
  @Input() nodata: any = {};
  @Input() loading: any = {};
  @Input() iready: any = {};
  /** Emitted when no peergroup exists and the parent must narrow its column list. */
  @Output() arrayFilterChange = new EventEmitter<string[]>();

  peergroup: any;
  customScore: string;
  screenSize$ = this.breakpointService.screenSize$;

  constructor(
    private router: Router,
    private compositeData: CompositeDataService,
    public routing: RoutingService,
    public featureFlagsService: FeatureFlagsService,
    private portfolioApi: PortfolioApiService,
    private breakpointService: BreakpointService,
    private scoreTimeSeriesService: ScoreTimeSeriesService,
    private scoreUtils: ScoreUtilsService,
    private cdr: ChangeDetectorRef,
    private destroyRef: DestroyRef,
  ) {
    this.customScore = this.featureFlagsService.scorelabSettings.defaultScore;
    this.destroyRef.onDestroy(() => this.cancel$.complete());
  }

  private loadedContext: PeergroupContext | null = null;
  // Cancels the in-flight peergroup/score fetch on a new load or when hidden.
  private cancel$ = new Subject<void>();

  ngOnChanges(changes: SimpleChanges): void {
    // Hidden (e.g. type change dropped Peergroup from the menu): kill any
    // in-flight fetch and clear state so it can't linger empty / flash stale.
    if (changes['visible'] && !this.visible) {
      this.onHidden();
    }

    if (changes['fetchParams'] && this.fetchParams) {
      this.peergroup = undefined;
      this.customScore = this.featureFlagsService.scorelabSettings.defaultScore;
      this.loadedContext = null;
    }

    if (
      (changes['context'] || changes['visible']) &&
      this.visible &&
      this.context &&
      this.context !== this.loadedContext
    ) {
      this.loadedContext = this.context;
      this.loadPeergroup(this.context);
    }
  }

  private onHidden(): void {
    this.cancel$.next();
    this.loading['Peergroup Comparison'] = false;
    this.iready['Peergroup Comparison'] = false;
    this.nodata['Peergroup Comparison'] = false;
    this.loadedContext = null;
    this.cdr.markForCheck();
  }

  private loadPeergroup(context: PeergroupContext): void {
    this.cancel$.next();
    const qparam = context.qparam;
    const entity = context.entity;

    if (
      ['industry', 'supersector', 'sector', 'subsector'].includes(qparam.type)
    ) {
      const peergroupArray = this.portfolioApi.activePortfolio.contentInfo.map(
        (el) => {
          return {
            entity: el,
          };
        },
      );
      this.peergroup = peergroupArray;
      this.iready['Peergroup Comparison'] = true;
      this.cdr.markForCheck();
    } else if (
      !(this.routing.isFolio() && !qparam.portfolioId) &&
      !(qparam.type === 'hierarchy')
    ) {
      const iparams = JSON.parse(JSON.stringify(qparam));

      if (iparams.type && iparams.id) {
        this.compositeData
          .peergroupComparison(iparams)
          .pipe(takeUntil(this.cancel$), takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: (peergroup) => {
              const peergroupChildren = peergroup
                .map((el) =>
                  Object.assign({}, el, {
                    entity: {
                      type: el.type,
                      name: el.name,
                      alpha_id: el.alpha_id,
                      compound_key: el.compound_key,
                    },
                  }),
                )
                .filter((children) => {
                  return children.entity.type === 'company';
                });
              if (peergroupChildren && peergroupChildren.length > 0) {
                this.peergroup = peergroupChildren.filter((item) => {
                  if (item.entity) {
                    if (item.entity.alpha_id !== qparam.id) {
                      return item;
                    } else {
                      item.count = Infinity;
                      return item;
                    }
                  }
                });
                const content = this.peergroup.map((el) => {
                  return el.entity.compound_key;
                });
                if (this.customScore && this.customScore !== 'sentiment') {
                  const resolvedScore = this.scoreUtils.resolveScoreType(
                    this.customScore,
                  );
                  this.scoreTimeSeriesService
                    .getScoreTimeSeries({
                      customParams: {}, // No collapse_on for peergroup comparison
                      body: {
                        score_type: resolvedScore,
                        entity: content,
                        time: iparams.time || '30',
                      },
                    })
                    .pipe(
                      map((response) => {
                        // Transform v2 response to legacy format
                        return response.score_ts.map((item) => ({
                          entity_info: item.entity_info,
                          score_ts_last_score: {
                            score:
                              item.score_ts.length > 0
                                ? item.score_ts[item.score_ts.length - 1].score
                                : null,
                          },
                        }));
                      }),
                      catchError(() => of([])),
                      takeUntil(this.cancel$),
                      takeUntilDestroyed(this.destroyRef),
                    )
                    .subscribe((res) => {
                      res.forEach((entityScore) => {
                        if (
                          entity.compound_key ===
                          entityScore.entity_info.compound_key
                        ) {
                          entity.score =
                            entityScore.score_ts_last_score.score?.toFixed(1);
                        }
                        this.peergroup.forEach((item, index) => {
                          if (
                            item.entity.compound_key ===
                            entityScore.entity_info.compound_key
                          ) {
                            this.peergroup[index].score =
                              entityScore.score_ts_last_score.score?.toFixed(1);
                          }
                        });
                      });
                      this.iready['Peergroup Comparison'] = true;
                      this.cdr.markForCheck();
                    });
                } else {
                  this.iready['Peergroup Comparison'] = true;
                  this.cdr.markForCheck();
                }
              } else {
                this.arrayFilterChange.emit(this.narrowedArrayFilter());
                this.iready['Peergroup Comparison'] = true;
                this.cdr.markForCheck();
              }
            },
            error: () => {
              this.nodata['Peergroup Comparison'] = true;
              this.loading['Peergroup Comparison'] = false;
              this.cdr.markForCheck();
            },
          });
      } else {
        this.arrayFilterChange.emit(this.narrowedArrayFilter());
        this.iready['Peergroup Comparison'] = null;
        this.cdr.markForCheck();
      }
    } else {
      this.arrayFilterChange.emit(this.narrowedArrayFilter());
      this.iready['Peergroup Comparison'] = true;
      this.cdr.markForCheck();
    }
  }

  /**
   * Column-narrowing when no peergroup is available: only the unified
   * 'Score Events' toggle remains (the parent hides the menu when ≤1 option).
   */
  private narrowedArrayFilter(): string[] {
    return ['Score Events'];
  }

  getScoreType(id?: string) {
    return getScoreType(this.featureFlagsService.scorelabSettings, id);
  }

  /** redirect specific entity */
  iEntity(type, id) {
    this.router.navigate([], {
      queryParams: {
        type: type,
        id: id,
        tag: null,
        news: null,
        eventid: null,
      },
      queryParamsHandling: 'merge',
    });
  }
}
